/* eslint-disable react-hooks/exhaustive-deps */
import {
  Column,
  ColumnDef,
  ColumnSort,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row as RTRow,
  RowSelectionState,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { Formik } from 'formik';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  Accordion,
  Button,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  InputGroup,
  Row,
  Spinner,
  Stack
} from 'react-bootstrap';
import {
  BsChevronDoubleLeft,
  BsChevronDoubleRight,
  BsChevronLeft,
  BsChevronRight,
  BsSearch
} from 'react-icons/bs';
import { HiOutlineAdjustmentsHorizontal } from 'react-icons/hi2';
import { PiCaretDownFill, PiCaretUpDownFill, PiCaretUpFill } from 'react-icons/pi';
import { useNavbar } from '../../context/DashNavbarContext';
import { FadeIn } from '../../features/animations/FadeIn';
import { downloadIndex } from '../../services/api';
import { Filter, GetQueryParams } from '../../types/api.type';
import { ButtonData } from '../buttonGroup/ButtonGroup';
import { FilterGroup } from '../filterGroup/FilterGroup';
import { DashLogo } from '../generic';
import './searchstatus.css';
import './SFCTable.css';
import { SimpleTable } from './SimpleTable';
export interface RowFormProps {
  validationSchema: any;
  onSubmit: (values: any, rowOriginal: any) => void;
  getInitialValues: (row: RTRow<any>) => any;
}

// Custom checkbox component for selecting rows
const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }: any, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef: any = ref || defaultRef;

  React.useEffect(() => {
    if (resolvedRef.current) {
      resolvedRef.current.indeterminate = indeterminate;
    }
  }, [resolvedRef, indeterminate]);

  return (
    <Form.Check
      type="checkbox"
      ref={resolvedRef}
      {...rest}
      style={{ margin: 0 }} // Optional styling to remove any margin
    />
  );
});

interface SFCTableProps {
  name?: string;
  columns: ColumnDef<any>[];
  data: any;
  count?: number;
  defaultScope?: string;
  scopes?: ButtonData[][];
  isLoading?: boolean;
  isFetching?: boolean;
  expandedItem?: { columns: ColumnDef<any>[]; rowKey: string; filterFn: Function };
  filterForm?: ReactElement;
  onFiltersChanged?: React.Dispatch<React.SetStateAction<GetQueryParams>>;
  newItemEl?: ReactNode;
  rowFormProps?: RowFormProps;
  selectableRows?: boolean;
  batchActions?: { name: string; fn: Function }[];
  indexDownloadPath?: string;
  filters?: Filter[];
  courseFilters?: Filter[];
  sidebars?: ReactNode[];
  defaultSort?: ColumnSort;
  searchbarActive?: boolean;
  page_Size?: number;
  onScopeChange?: (scope: string) => void;
  showScope?: boolean;
  getRowId?: (originalRow: any, index: number) => string;
  /** Wider desktop toolbar column (lg breakpoint). Buttons align right and expand leftward. */
  toolbarColLg?: number;
}
const styles = {
  container: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    maxWidth: '600px',
    margin: '16px',
    padding: '20px',
    backgroundColor: '#ffffff',
    border: '1px solid #e1e1e1',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  header: {
    margin: '0 0 16px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#333333'
  },
  section: {
    marginBottom: '20px'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px'
  },
  label: {
    fontWeight: '600',
    marginRight: '10px',
    color: '#555555',
    minWidth: '60px'
  },
  value: {
    color: '#333333',
    fontSize: '14px',
    fontWeight: '600'
  },
  subheader: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333333'
  },
  list: {
    listStyleType: 'none',
    padding: '0',
    margin: '0'
  },
  listItem: {
    padding: '6px 0',
    borderBottom: '1px solid #f0f0f0',
    color: '#555555'
  }
};
const SFCTable: React.FC<SFCTableProps> = ({
  name,
  columns,
  data,
  count,
  defaultScope,
  scopes,
  isLoading,
  isFetching,
  expandedItem,
  filterForm,
  onFiltersChanged,
  newItemEl,
  rowFormProps,
  selectableRows,
  indexDownloadPath,
  filters,
  courseFilters,
  batchActions,
  sidebars,
  defaultSort,
  searchbarActive = false,
  page_Size,
  onScopeChange,
  showScope = false,
  getRowId: getRowIdProp,
  toolbarColLg
}) => {
  const { activeNavbar, setActiveNavbar, setFilterNavContent, setFilterNavOverlap } = useNavbar();
  const resolvedToolbarColLg = toolbarColLg ?? (searchbarActive ? 6 : newItemEl ? 6 : 3);
  const toolbarStackClassName =
    toolbarColLg != null || newItemEl ? 'sfc-table-toolbar-actions' : undefined;
  const [globalFilter, setGlobalFilter] = useState('');
  const [scope, setScope] = useState(defaultScope ?? '');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: page_Size ?? 10
  });
  const [sorting, setSorting] = useState<SortingState>(defaultSort ? [defaultSort] : []);
  const [orderBy, setOrderBy] = useState<{ field: string; order: string } | undefined>();
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});

  useEffect(() => {
    if (!isLoading && defaultSort) {
      let id = defaultSort.id;
      let order = !defaultSort.desc ? 'asc' : 'desc';
      setOrderBy({ field: id, order });
    }
  }, [isLoading]);

  useEffect(() => {
    // Set filter nav content to null on unmount
    return () => {
      setFilterNavContent(null);
      setFilterNavOverlap(false);
    };
  }, []);

  useEffect(() => {
    if (filterForm) {
      setFilterNavContent(filterForm);
      setFilterNavOverlap(!!sidebars?.length);
    }
  }, [filterForm]);

  useEffect(() => {
    // if (scope === '') return;
    onFiltersChanged?.((old) => ({
      ...old,
      scope,
      data: {
        ...old?.data,
        orderBy: orderBy?.field ? orderBy : old.data?.orderBy,
        page: pagination.pageIndex + 1,
        entries: pagination.pageSize
      }
    }));
  }, [pagination, scope, orderBy]);

  const download = (format: 'json' | 'csv') => {
    if (indexDownloadPath) {
      downloadIndex({
        path: indexDownloadPath,
        scope: scope,
        body: { orderBy, filterBy: filters },
        format,
        method: 'POST'
      });
    }
  };

  const {
    getHeaderGroups,
    getRowModel,
    getState,
    getPageCount,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    previousPage,
    nextPage,
    firstPage,
    lastPage,
    setPageIndex,
    setPageSize,
    getSelectedRowModel,
    getIsAllRowsSelected,
    getIsSomeRowsSelected,
    getToggleAllPageRowsSelectedHandler
  } = useReactTable({
    data,
    columns,
    getRowId:
      getRowIdProp ??
      ((originalRow, index) => (originalRow?.id != null ? String(originalRow.id) : String(index))),
    state: {
      globalFilter,
      pagination,
      sorting,
      rowSelection
    },
    manualPagination: true,
    onPaginationChange: setPagination, //update the pagination state when internal APIs mutate the pagination state
    rowCount: count,
    manualSorting: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true
  });

  const getCanLastPage = () => {
    return getState().pagination.pageIndex !== getPageCount() - 1;
  };

  const getCanFirstPage = () => {
    return getState().pagination.pageIndex !== 0;
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const pageIndex = getState().pagination.pageIndex;

    if (getPageOptions().length <= 3) {
      // If total pages are less than or equal to 3, show all pages
      for (let i = 0; i < getPageOptions().length; i++) {
        pageNumbers.push(
          <button
            key={i}
            onClick={() => setPageIndex(i)}
            className={`rounded-1 mx-1 border-0 ${i === pageIndex ? 'active' : ''}`}
            style={{
              background: i === pageIndex ? '#F2F2F2' : 'transparent',
              color: i === pageIndex ? '#282828' : '#888888',
              fontSize: '.75rem'
            }}
          >
            {i + 1}
          </button>
        );
      }
    } else {
      // If more than 3 pages, dynamically adjust the page numbers displayed
      const startPage =
        pageIndex === 0
          ? pageIndex
          : pageIndex === getPageOptions().length - 1
            ? pageIndex - 2
            : pageIndex - 1;
      const endPage =
        pageIndex === 0
          ? pageIndex + 2
          : pageIndex === getPageOptions().length - 1
            ? pageIndex
            : pageIndex + 1;

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <button
            key={i}
            onClick={() => setPageIndex(i)}
            className={`rounded-1 mx-1 border-0 ${i === pageIndex ? 'active' : ''}`}
            style={{
              background: i === pageIndex ? '#F2F2F2' : 'transparent',
              color: i === pageIndex ? '#282828' : '#888888',
              fontSize: '.75rem'
            }}
          >
            {i + 1}
          </button>
        );
      }
    }

    return pageNumbers;
  };

  const handleSortingChange = (id: string, column: Column<any, unknown>) => {
    const nextSorting = column.getNextSortingOrder();
    if (!nextSorting) setOrderBy(undefined);
    else setOrderBy({ field: id, order: nextSorting });
    column.toggleSorting();
  };

  const selectedRowCount = getSelectedRowModel().rows.length;
  const isAtLeastOneRowSelected = selectedRowCount > 0;

  const selectedRowsIDs = getRowModel()
    .rows.filter((row) => Object.keys(rowSelection).includes(row.id))
    .map((el) => el.original.id);

  const tableContent = (
    <React.Fragment>
      <Stack
        className="d-lg-none mb-3"
        gap={3}
      >
        <Stack
          direction="horizontal"
          gap={2}
        >
          <h6
            className="fw-bolder text-secondary mb-lg-0 ps-lg-2 mb-0 ps-0"
            style={{ fontSize: '1.125rem' }}
          >
            {name || scopes?.[0]?.[0]?.title}
          </h6>
          <span
            className="rounded-4 fw-bold px-2 py-1"
            style={{ background: '#F2F4F7', color: '#888888', fontSize: '.75rem' }}
          >
            {count} items
          </span>
        </Stack>

        <Stack
          direction="horizontal"
          gap={2}
          className={toolbarStackClassName}
        >
          {searchbarActive && (
            <InputGroup>
              <InputGroup.Text>
                <BsSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search"
                aria-label="Search"
                name="search"
                style={{ height: 40 }}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                }}
              />
            </InputGroup>
          )}
          {filterForm && (
            <Button
              variant="outline-primary"
              onClick={() => setActiveNavbar('filter')}
              className={`d-flex align-items-center text-uppercase flex-shrink-0 ${activeNavbar === 'filter' ? 'active' : ''}`}
            >
              <HiOutlineAdjustmentsHorizontal
                size={20}
                className="me-2"
              />
              Filter
            </Button>
          )}
          {newItemEl && newItemEl}
        </Stack>
        {/* <span className="text-secondary d-lg-none">Click on a item to view details</span> */}
      </Stack>

      <Row className="d-none d-lg-flex align-items-center justify-content-between p-4">
        <Col
          xs={12}
          lg={6}
          className="p-0"
        >
          <Stack
            direction="horizontal"
            gap={2}
          >
            <h6
              className="fw-bolder text-secondary mb-lg-0 mb-0"
              style={{ fontSize: '1.125rem' }}
            >
              {name || scopes?.[0]?.[0]?.title}
            </h6>
            <span
              className="rounded-4 fw-bold px-2 py-1"
              style={{ background: '#F2F4F7', color: '#888888', fontSize: '.75rem' }}
            >
              {count} items
            </span>
          </Stack>
        </Col>

        <Col
          xs={12}
          lg={resolvedToolbarColLg}
          className="p-0"
        >
          <Stack
            direction="horizontal"
            gap={2}
            className={toolbarStackClassName}
          >
            {searchbarActive && (
              <InputGroup>
                <InputGroup.Text>
                  <BsSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search (only visible column data)"
                  aria-label="Search"
                  name="search"
                  style={{ height: 40 }}
                  onChange={(e) => {
                    setGlobalFilter(e.target.value);
                  }}
                />
              </InputGroup>
            )}
            {filterForm && (
              <Button
                variant="outline-primary"
                onClick={() => setActiveNavbar('filter')}
                className={`d-flex align-items-center text-uppercase flex-shrink-0 ${activeNavbar === 'filter' ? 'active' : ''} ${
                  !searchbarActive && !newItemEl && toolbarColLg == null ? 'ms-auto' : ''
                }`}
              >
                <HiOutlineAdjustmentsHorizontal
                  size={20}
                  className="me-2"
                />
                Filter
              </Button>
            )}
            {newItemEl && newItemEl}
          </Stack>
        </Col>
      </Row>

      <div style={{ position: 'relative' }}>
        <Accordion className="overflow-auto">
          <table className="mb-0 table">
            <thead>
              {getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {selectableRows && (
                    <th>
                      <IndeterminateCheckbox
                        {...{
                          checked: getIsAllRowsSelected(),
                          indeterminate: getIsSomeRowsSelected(),
                          onChange: getToggleAllPageRowsSelectedHandler()
                        }}
                      />
                    </th>
                  )}
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className="text-truncate ff-inter"
                      style={{ whiteSpace: 'pre' }}
                    >
                      <div
                        className="clickable"
                        onClick={() => handleSortingChange(header.id, header.column)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() &&
                          ({
                            asc: <PiCaretUpFill className="mb-1 ms-2" />,
                            desc: <PiCaretDownFill className="mb-1 ms-2" />,
                            false: <PiCaretUpDownFill className="mb-1 ms-2" />
                          }[header.column.getIsSorted() as string] ??
                            null)}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading && getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (selectableRows ? 1 : 0)}
                    style={{ height: 300, border: 'none' }}
                  />
                </tr>
              ) : (
                getRowModel().rows.map((row) => {
                  let rowContent = (
                    <tr
                      key={row.id}
                      className="align-middle"
                    >
                      {selectableRows && (
                        <td>
                          <IndeterminateCheckbox
                            {...{
                              checked: row.getIsSelected(),
                              indeterminate: row.getIsSomeSelected(),
                              onChange: row.getToggleSelectedHandler()
                            }}
                          />
                        </td>
                      )}
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                  return (
                    <React.Fragment key={row.id}>
                      {rowFormProps ? (
                        <Formik
                          key={row.id}
                          initialValues={rowFormProps.getInitialValues(row)}
                          validationSchema={rowFormProps.validationSchema}
                          onSubmit={(values, { setSubmitting }) => {
                            rowFormProps.onSubmit(values, row.original);
                            setSubmitting(false);
                          }}
                        >
                          {() => rowContent}
                        </Formik>
                      ) : (
                        rowContent
                      )}
                      {expandedItem && row.getIsExpanded() ? (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="p-0"
                          >
                            <SimpleTable
                              columns={expandedItem.columns}
                              data={
                                expandedItem.filterFn
                                  ? row.original[expandedItem.rowKey].filter(expandedItem.filterFn)
                                  : row.original[expandedItem.rowKey]
                              }
                            />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </Accordion>

        {(isLoading || isFetching) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.6)',
              zIndex: 10
            }}
          >
            <Spinner
              animation="border"
              variant="primary"
            />
          </div>
        )}
      </div>
      {data.length > 0 && (
        <Row className="align-items-center p-lg-4 my-lg-0 my-3">
          <Col
            xs={7}
            sm={6}
          >
            <Stack
              direction="horizontal"
              gap={2}
              className="align-items-center sfc-table-page-size"
            >
              <span
                className="fw-normal ms-lg-0 ms-2 flex-shrink-0"
                style={{ fontSize: '.875rem', color: '#344054' }}
              >
                Viewing:
              </span>
              <Form.Select
                id="count"
                value={pagination.pageSize}
                onChange={(e) => setPageSize(+e.target.value)}
                className="pagination py-0"
              >
                {[5, 10, 20, 50].map((item) => (
                  <option
                    value={item}
                    key={item}
                  >
                    {item}
                  </option>
                ))}
              </Form.Select>
              <span
                className="d-none d-lg-block"
                style={{ fontSize: '.875rem', color: '#344054' }}
              >
                {`Page ${getState().pagination.pageIndex + 1} of ${getPageCount()}`}
              </span>
              {indexDownloadPath && (
                <div
                  className="d-none d-lg-block"
                  style={{ fontSize: '.875rem', color: '#344054' }}
                >
                  <span className={'p-1'}>Download:</span>
                  <a
                    href="#"
                    className={'p-1'}
                    onClick={() => download?.('json')}
                  >
                    JSON
                  </a>
                  <a
                    href="#"
                    className={'p-1'}
                    onClick={() => download?.('csv')}
                  >
                    CSV
                  </a>
                </div>
              )}
            </Stack>
          </Col>

          <Col
            xs={5}
            sm={6}
          >
            <Stack
              direction="horizontal"
              className="justify-content-center justify-content-lg-end"
            >
              <button
                className="btn btn-link link-secondary px-0 py-0"
                onClick={() => firstPage()}
                hidden={!getCanFirstPage()}
              >
                <BsChevronDoubleLeft />
              </button>
              <button
                className="btn btn-link link-secondary px-0 py-0"
                onClick={() => previousPage()}
                hidden={!getCanPreviousPage()}
              >
                <BsChevronLeft />
              </button>
              <div className="d-flex align-items-center pt-1">{renderPageNumbers()}</div>
              <button
                className="btn btn-link link-secondary px-0 py-0"
                onClick={() => nextPage()}
                hidden={!getCanNextPage()}
              >
                <BsChevronRight />
              </button>
              <button
                className="btn btn-link link-secondary px-0 py-0"
                onClick={() => lastPage()}
                hidden={!getCanLastPage()}
              >
                <BsChevronDoubleRight />
              </button>
            </Stack>
          </Col>
        </Row>
      )}
    </React.Fragment>
  );

  const snakeToTitleCase = (str: string) => {
    if (!str) return '';

    return str
      .split('_') // Split by underscore
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(' '); // Join with spaces
  };
  return (
    <FadeIn>
      <Row>
        {batchActions && (
          <Col
            xs="auto"
            lg="auto"
          >
            <DropdownButton
              id="dropdown-basic-button"
              title="Batch actions"
              size="sm"
              variant="info"
              disabled={!selectableRows || !isAtLeastOneRowSelected}
            >
              {batchActions.map((action) => (
                <Dropdown.Item
                  key={action.name}
                  onClick={() => action.fn(selectedRowsIDs)}
                >
                  {action.name}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </Col>
        )}

        <Col
          xs="auto"
          lg="auto"
        >
          {scopes && scopes.length > 0 && (
            <FilterGroup
              filterGroups={scopes}
              defaultScope={defaultScope}
              onFilterSelect={(filter: string) => {
                setScope(filter);
                if (onScopeChange) {
                  onScopeChange(filter);
                }
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: 0
                }));
              }}
            />
          )}
        </Col>
      </Row>

      {/* Desktop View */}
      <div
        className="d-none d-lg-flex"
        style={{ width: '100%' }}
      >
        <div
          className="sfc-table px-2 py-1"
          style={{
            flexGrow: 1,
            maxWidth:
              (sidebars?.length ?? 0) > 0 && showScope
                ? 'calc(100% - 500px)' // 300px (sidebar) + 200px (new panel)
                : (sidebars?.length ?? 0) > 0
                  ? 'calc(100% - var(--sfc-table-sidebar, 300px))'
                  : showScope
                    ? 'calc(100% - 200px)'
                    : '100%'
          }}
        >
          {tableContent}
        </div>

        {/* Sidebar */}
        {(sidebars?.length ?? 0) > 0 && (
          <div className="py-0 ps-3">
            <Stack
              gap={4}
              className="p-3"
              style={{
                width: 'var(--sfc-table-sidebar, 300px)',
                background: '#F2F2F2',
                borderRadius: '10px',
                boxShadow: '0 0 8px 0 rgba(0, 0, 0, .1), 0 0 8px 0 rgba(0, 0, 0, .1)'
              }}
            >
              {sidebars}
              <DashLogo />
            </Stack>
          </div>
        )}

        {/* Scope Info Panel */}
        {showScope && (
          <div className="py-0 ps-3">
            <div
              style={{
                width: 200,
                height: 400,
                backgroundColor: '#fff',
                border: '1px solid #ced4da',
                borderRadius: '8px',
                padding: '1rem',
                overflowY: 'auto'
              }}
            >
              <h2 style={styles.header}>
                <strong>Search status:</strong>
              </h2>

              <div style={styles.section}>
                <div style={styles.row}>
                  <span style={styles.label}>Scope:</span>
                  <span style={styles.value}>
                    <strong>{snakeToTitleCase(scope)}</strong>
                  </span>
                </div>
              </div>

              {filters && !courseFilters && (
                <div style={styles.section}>
                  <h2 style={styles.subheader}>
                    <strong>Current filters:</strong>
                  </h2>
                  <ul style={styles.list}>
                    {filters.length === 0 ? (
                      <p>No filters applied</p>
                    ) : (
                      filters.map((filter, index) => (
                        <li
                          key={index}
                          style={styles.listItem}
                        >
                          <span>
                            {snakeToTitleCase(filter.field)} {snakeToTitleCase(filter.operator)} :
                            <strong> {filter.value}</strong>
                          </span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}

              {courseFilters && (
                <div style={styles.section}>
                  <h2 style={styles.subheader}>
                    <strong>Current filters:</strong>
                  </h2>
                  <ul style={styles.list}>
                    {courseFilters.length === 0 ? (
                      <p>No filters applied</p>
                    ) : (
                      courseFilters.map((filter, index) => (
                        <li
                          key={index}
                          style={styles.listItem}
                        >
                          <span>
                            {snakeToTitleCase(filter.field)} {snakeToTitleCase(filter.operator)} :
                            <strong> {filter.value}</strong>
                          </span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile View */}
      <div className="d-flex d-lg-none flex-column w-100 px-2">
        <div style={{ overflowX: 'auto' }}>{tableContent}</div>
      </div>
    </FadeIn>
  );
};

export default SFCTable;
